/* ============================================================
   WowSticker — Cut-aware live preview (100% client-side, canvas 2D)
   Standalone module: window.WowStickerCutPreview
   · Background removal: alpha mask (PNG) · border flood-fill (opaque uniform)
     · graceful fallback (complex bg) — never blocks the order
   · Cut modes: contour (sagomato) · rect (quadrato) · circle (tondo) · sheet (kiss-cut)
   · Material finishes: olografica · laminato · uvgloss · glow
   · One-time cutout, cached; changing cut/size re-renders from cache
   No external APIs, no libraries, no backend, no localStorage.
   Back-compat alias: window.WowPreview (register/update/setImage/hasImage/clear)
   ============================================================ */
(function(){
  'use strict';

  var RES = 560;                 // render canvas resolution
  var PROC = 900;                // max side for the cutout pipeline
  // Adaptive white-border thickness in REAL millimetres, by the sticker's SHORT side:
  //   short side ≤ 5 cm → 2 mm · above 5 cm → 3 mm
  // Single source of truth for die-cut, kiss-cut, dark-bg plate and the optional rect/circle border.
  function borderMM(physShortCm){ return (physShortCm != null && physShortCm <= 5) ? 2 : 3; }
  var targets = [];              // registered preview canvases

  // per-image cache (built once by setImage)
  var art = null;                // {canvas, w, h}  — background-removed subject (RGBA)
  var artOrig = null;            // canvas — normalized image BEFORE background removal (bg included)
  var artTransp = null;          // lazy: subject with white→transparent + ~90% coverage (Trasparente)
  var vinylMask = null;          // lazy: Prespaziati subject by colour-similarity (enclosed holes open)
  var artOrigTransp = null;      // lazy: full image with white→transparent + ~90% coverage (Trasparente)
  var lightRatio = 0;            // fraction of the graphic that is very light (near-white)
  var kissBorderMode = null;     // mezzo taglio sagomato: 'white' (bordo bianco) | 'flat' (sfondo pieno)
  var artBBox = null;            // {x,y,w,h} normalized 0..1 bounding box of subject
  var bgColor = null;            // sampled uniform background color [r,g,b]
  var bgIsDark = false;          // background is dark/colored (part of the design)
  var bgRemovable = false;       // a uniform background was detected & removed
  var hasImg = false;
  var hasAlpha = false;          // the uploaded file has native transparency (graphic without background)
  var fallback = false;          // true = complex bg, no auto cutout
  var lastState = {};
  var lastBase = null;           // last composed single-sticker canvas (for getCutoutDataURL)
  var lastPrintOnly = null;      // DTF: the artwork on transparent (production export, no film scenery)
  var holoRAF = null;
  var cbs = { onProcessing:null, onReady:null, onFallback:null };

  /* ---------- small helpers ---------- */
  function mkCanvas(w,h){ var c=document.createElement('canvas'); c.width=w; c.height=h; return c; }
  function fire(name, arg){ if(typeof cbs[name]==='function'){ try{ cbs[name](arg); }catch(e){} } }
  function sizeRatio(state){
    var s = state && state.size;
    if(s==='custom'){ var w=+state.customW, h=+state.customH; return (w>0&&h>0)? w/h : 1; }
    if(typeof s==='string' && s.indexOf('x')>0){
      var p=s.split('x'); var a=+p[0],b=+p[1];
      if(a>0&&b>0){
        var ratio = a/b;   // declared as width×height (landscape orientation)
        // Portrait flips the cut-shape proportions — EXCEPT for a contour (sagomato)
        // die-cut, which follows the subject and ignores the declared orientation.
        var mode = state && state.shapeMode;
        if(state && state.orientamento==='portrait' && a!==b && mode!=='contour' && mode!=='sheet'){
          ratio = b/a;
        }
        return ratio;
      }
    }
    return 1;
  }
  // physical size in cm (orientation-applied) — drives the real-millimetre border scaling
  function physSize(state){
    var s = state && state.size;
    if(s==='custom'){ var w=+state.customW, h=+state.customH; if(w>0&&h>0) return {w:w,h:h}; return {w:5,h:5}; }
    if(typeof s==='string' && s.indexOf('x')>0){
      var p=s.split('x'); var a=+p[0], b=+p[1];
      if(a>0&&b>0){ if(state.orientamento==='portrait' && a!==b) return {w:b,h:a}; return {w:a,h:b}; }
    }
    return {w:5,h:5};
  }
  // border thickness in px: bordoPx = (BORDER_MM / shortSideMm) × shortSidePx.
  // pixels-per-cm is uniform (aspect preserved), so box.w/physW == box.h/physH; the mm value
  // is chosen adaptively from the physical short side.
  function borderPx(box, physW, physH){
    if(physW>0 && physH>0){
      var pxPerMm = (box.w/physW)/10;          // px per cm ÷ 10
      var shortCm = Math.min(physW, physH);
      return Math.max(2, borderMM(shortCm) * pxPerMm);
    }
    return Math.max(9, box.w*0.03);
  }

  /* ---------- cutout pipeline (runs once per image) ---------- */
  function detectAlpha(img){
    var c=mkCanvas(64,64), x=c.getContext('2d');
    x.drawImage(img,0,0,64,64);
    var d=x.getImageData(0,0,64,64).data, t=0;
    for(var i=3;i<d.length;i+=4){ if(d[i]<200) t++; }
    return t > 64*64*0.04;
  }
  function dist2(r,g,b, r2,g2,b2){ var dr=r-r2,dg=g-g2,db=b-b2; return dr*dr+dg*dg+db*db; }

  // sample the border ring → {mean:[r,g,b], variance, ok}
  function borderStats(d,w,h){
    var xs=[], step=Math.max(1,Math.floor(w/60)), stepY=Math.max(1,Math.floor(h/60));
    function push(px,py){ var i=(py*w+px)*4; xs.push([d[i],d[i+1],d[i+2]]); }
    for(var x=0;x<w;x+=step){ push(x,0); push(x,h-1); }
    for(var y=0;y<h;y+=stepY){ push(0,y); push(w-1,y); }
    var n=xs.length, m=[0,0,0], k;
    for(k=0;k<n;k++){ m[0]+=xs[k][0]; m[1]+=xs[k][1]; m[2]+=xs[k][2]; }
    m[0]/=n; m[1]/=n; m[2]/=n;
    var v=0; for(k=0;k<n;k++){ v+=dist2(xs[k][0],xs[k][1],xs[k][2], m[0],m[1],m[2]); }
    v/=n;
    return { mean:m, variance:v };
  }
  // dominant border colour by clustering (works when the subject touches the edges → mean is skewed).
  // Returns { color:[r,g,b], frac } where frac = share of border samples within `tol` of that colour.
  function dominantBorderColor(d,w,h,tol){
    var xs=[], step=Math.max(1,Math.floor(w/60)), stepY=Math.max(1,Math.floor(h/60));
    function push(px,py){ var i=(py*w+px)*4; xs.push([d[i],d[i+1],d[i+2]]); }
    for(var x=0;x<w;x+=step){ push(x,0); push(x,h-1); }
    for(var y=0;y<h;y+=stepY){ push(0,y); push(w-1,y); }
    var n=xs.length, tol2=tol*tol, best=null, bestCnt=-1;
    for(var a=0;a<n;a++){
      var cnt=0, sr=0,sg=0,sb=0;
      for(var b=0;b<n;b++){ if(dist2(xs[a][0],xs[a][1],xs[a][2], xs[b][0],xs[b][1],xs[b][2])<=tol2){ cnt++; sr+=xs[b][0]; sg+=xs[b][1]; sb+=xs[b][2]; } }
      if(cnt>bestCnt){ bestCnt=cnt; best=[sr/cnt,sg/cnt,sb/cnt]; }
    }
    return best ? { color:best, frac:bestCnt/n } : null;
  }

  // border-connected flood fill → Uint8 mask (1 = background to remove)
  function floodBg(d,w,h,bg,tol){
    var mask=new Uint8Array(w*h);       // 0 unknown, 1 bg
    var vis=new Uint8Array(w*h);
    var stack=[], tol2=tol*tol, i, px, py;
    function seed(px,py){ var p=py*w+px; if(!vis[p]){ vis[p]=1; stack.push(p); } }
    for(px=0;px<w;px++){ seed(px,0); seed(px,h-1); }
    for(py=0;py<h;py++){ seed(0,py); seed(w-1,py); }
    while(stack.length){
      var p=stack.pop(); i=p*4;
      if(dist2(d[i],d[i+1],d[i+2], bg[0],bg[1],bg[2]) > tol2) continue;
      mask[p]=1;
      px=p%w; py=(p-px)/w;
      if(px>0){ var l=p-1; if(!vis[l]){ vis[l]=1; stack.push(l); } }
      if(px<w-1){ var r=p+1; if(!vis[r]){ vis[r]=1; stack.push(r); } }
      if(py>0){ var u=p-w; if(!vis[u]){ vis[u]=1; stack.push(u); } }
      if(py<h-1){ var dn=p+w; if(!vis[dn]){ vis[dn]=1; stack.push(dn); } }
    }
    return mask;
  }

  // light 1px feather of the subject alpha for smoother edges
  function feather(id,w,h){
    var d=id.data, out=new Uint8ClampedArray(d.length); out.set(d);
    for(var y=1;y<h-1;y++) for(var x=1;x<w-1;x++){
      var p=(y*w+x)*4+3;
      var a=(d[p]+d[p-4]+d[p+4]+d[p-w*4]+d[p+w*4])/5;
      out[p]=a;
    }
    id.data.set(out);
    return id;
  }

  function computeBBox(id,w,h){
    var d=id.data, minx=w,miny=h,maxx=0,maxy=0,found=false;
    for(var y=0;y<h;y++) for(var x=0;x<w;x++){
      if(d[(y*w+x)*4+3]>24){ found=true; if(x<minx)minx=x; if(x>maxx)maxx=x; if(y<miny)miny=y; if(y>maxy)maxy=y; }
    }
    if(!found) return {x:0,y:0,w:1,h:1};
    return { x:minx/w, y:miny/h, w:(maxx-minx+1)/w, h:(maxy-miny+1)/h };
  }

  function processImage(img){
    // normalize
    var scale=Math.min(1, PROC/Math.max(img.width,img.height));
    var w=Math.max(1,Math.round(img.width*scale)), h=Math.max(1,Math.round(img.height*scale));
    var c=mkCanvas(w,h), x=c.getContext('2d');
    x.drawImage(img,0,0,w,h);
    artOrig=mkCanvas(w,h); artOrig.getContext('2d').drawImage(c,0,0);   // keep the original (bg included)

    fallback=false; bgColor=null; bgIsDark=false; bgRemovable=false;
    var alpha = detectAlpha(img);
    hasAlpha = alpha;
    if(!alpha){
      var id=x.getImageData(0,0,w,h);
      var st=borderStats(id.data,w,h);
      var d=id.data, tol=46, tol2=tol*tol;
      if(st.variance <= 1400){
        // bordo uniforme → rimozione classica per connettività dal bordo
        var mask=floodBg(d,w,h,st.mean,tol);
        for(var p=0;p<mask.length;p++){ if(mask[p]) d[p*4+3]=0; }
        feather(id,w,h); x.putImageData(id,0,0);
        bgColor=[Math.round(st.mean[0]),Math.round(st.mean[1]),Math.round(st.mean[2])];
        var lum=(bgColor[0]+bgColor[1]+bgColor[2])/3, sat=Math.max(bgColor[0],bgColor[1],bgColor[2])-Math.min(bgColor[0],bgColor[1],bgColor[2]);
        bgIsDark = !(lum>232 && sat<26);
        bgRemovable=true;
      } else {
        // bordo NON uniforme: il soggetto tocca i bordi (es. logo con scritta a tutta larghezza).
        // Cerca il colore di sfondo DOMINANTE sul bordo; se copre la maggioranza, rimuovilo GLOBALMENTE
        // per similarità di colore (il soggetto può toccare i bordi) invece di rinunciare (fallback).
        var dom=dominantBorderColor(d,w,h,tol);
        if(dom && dom.frac>=0.55){
          for(var q=0;q<d.length;q+=4){ if(dist2(d[q],d[q+1],d[q+2], dom.color[0],dom.color[1],dom.color[2])<=tol2) d[q+3]=0; }
          feather(id,w,h); x.putImageData(id,0,0);
          bgColor=[Math.round(dom.color[0]),Math.round(dom.color[1]),Math.round(dom.color[2])];
          var lum2=(bgColor[0]+bgColor[1]+bgColor[2])/3, sat2=Math.max(bgColor[0],bgColor[1],bgColor[2])-Math.min(bgColor[0],bgColor[1],bgColor[2]);
          bgIsDark = !(lum2>232 && sat2<26);
          bgRemovable=true;
        } else {
          fallback=true;   // sfondo davvero complesso → nessun ritaglio automatico
        }
      }
    }
    art = { canvas:c, w:w, h:h };
    artBBox = fallback ? {x:0,y:0,w:1,h:1} : computeBBox(x.getImageData(0,0,w,h), w, h);
    artTransp = null; artOrigTransp = null; vinylMask = null;   // reset lazy caches

    // fraction of the graphic that is near-white / very light (drives the honest-color note)
    (function(){
      var basis = fallback ? artOrig : c;
      var bx = basis.getContext('2d').getImageData(0,0,basis.width,basis.height).data;
      var subj=0, light=0;
      for(var i=0;i<bx.length;i+=4){
        if(bx[i+3]>128){ subj++; var lum=(bx[i]+bx[i+1]+bx[i+2])/3; if(lum>228) light++; }
      }
      lightRatio = subj ? light/subj : 0;
    })();
  }

  // Trasparente honest-color transform: pure/near white → transparent (no white ink),
  // colored areas rendered at ~90% coverage. Result cached per source.
  function makeTransparent(src){
    var w=src.width, h=src.height, c=mkCanvas(w,h), x=c.getContext('2d');
    x.drawImage(src,0,0);
    var id=x.getImageData(0,0,w,h), d=id.data;
    for(var i=0;i<d.length;i+=4){
      var a=d[i+3]; if(a===0) continue;
      var r=d[i], g=d[i+1], b=d[i+2];
      if(r>=245 && g>=245 && b>=245){ d[i+3]=0; }        // white ink prints as nothing on clear film
      else { d[i+3]=Math.round(a*0.9); }                  // lighter coverage on the clear film
    }
    x.putImageData(id,0,0); return c;
  }
  // Trasparente honest-white applies ONLY to real-transparency files (alpha PNG/PDF); opaque files print as-is.
  function transpSubject(){ if(!hasAlpha) return art?art.canvas:artOrig; if(!artTransp && art) artTransp = makeTransparent(art.canvas); return artTransp; }
  // soft eraser mask of near-white pixels (punch "white = not printed" holes on Trasparente, in every
  // render path including the dark-bg plate). Soft AA edges (no hard fringes).
  function whiteMaskOf(src){
    var w=src.width, h=src.height, c=mkCanvas(w,h), x=c.getContext('2d');
    x.drawImage(src,0,0); var id=x.getImageData(0,0,w,h), d=id.data;
    for(var i=0;i<d.length;i+=4){ var a=d[i+3]; if(!a){ continue; }
      var r=d[i],g=d[i+1],b=d[i+2], mx=Math.max(r,g,b), mn=Math.min(r,g,b), sat=mx-mn, lum=(r+g+b)/3;
      var t = Math.max(0,Math.min(1,(lum-225)/30)) * Math.max(0,Math.min(1,(40-sat)/40));
      d[i]=255; d[i+1]=255; d[i+2]=255; d[i+3]=Math.round(a*t);
    }
    x.putImageData(id,0,0); return c;
  }
  function transpFull(){ if(!hasAlpha) return artOrig; if(!artOrigTransp && artOrig) artOrigTransp = makeTransparent(artOrig); return artOrigTransp; }
  // Prespaziati subject mask: everything that differs from the sampled background colour becomes
  // vinyl — by COLOUR SIMILARITY, not border connectivity, so enclosed counters (A/R/O) stay open.
  function vinylSubject(){
    if(hasAlpha) return artOrig;                    // native alpha already defines the holes
    if(vinylMask) return vinylMask;
    if(!artOrig || !bgColor) return art ? art.canvas : artOrig;
    var w=artOrig.width, h=artOrig.height, c=mkCanvas(w,h), x=c.getContext('2d');
    x.drawImage(artOrig,0,0); var id=x.getImageData(0,0,w,h), d=id.data, tol2=46*46;
    for(var i=0;i<d.length;i+=4){ if(dist2(d[i],d[i+1],d[i+2], bgColor[0],bgColor[1],bgColor[2]) <= tol2) d[i+3]=0; }
    x.putImageData(id,0,0); vinylMask=c; return c;
  }

  /* ---------- drawing helpers ---------- */
  function drawContain(ctx,src,bx,by,bw,bh){
    var ar=src.width/src.height, w=bw, h=bw/ar;
    if(h>bh){ h=bh; w=bh*ar; }
    ctx.drawImage(src, bx+(bw-w)/2, by+(bh-h)/2, w, h);
  }
  function drawCover(ctx,src,bx,by,bw,bh){
    var ar=src.width/src.height, w=bw, h=bw/ar;
    if(h<bh){ h=bh; w=bh*ar; }
    ctx.drawImage(src, bx+(bw-w)/2, by+(bh-h)/2, w, h);
  }
  // print safety margin: important elements never touch the die line (typography standard)
  var SAFE_MARGIN_MM = 5;
  function safeMarginPx(box, physW){ return physW>0 ? SAFE_MARGIN_MM * ((box.w/physW)/10) : box.w*0.06; }
  // draw only the subject's trimmed bbox, contained + pixel-centered inside a target area
  function drawSubjectContain(ctx, src, bbox, tx, ty, tw, th){
    bbox = bbox || {x:0,y:0,w:1,h:1};
    var sx=bbox.x*src.width, sy=bbox.y*src.height,
        sw=Math.max(1,bbox.w*src.width), sh=Math.max(1,bbox.h*src.height);
    var ar=sw/sh, w=tw, h=tw/ar; if(h>th){ h=th; w=th*ar; }
    var dx=Math.round(tx+(tw-w)/2), dy=Math.round(ty+(th-h)/2);
    ctx.drawImage(src, sx,sy,sw,sh, dx, dy, Math.round(w), Math.round(h));
  }
  function roundRectPath(ctx,x,y,w,h,r){
    r=Math.min(r,w/2,h/2);
    ctx.beginPath();
    ctx.moveTo(x+r,y); ctx.arcTo(x+w,y,x+w,y+h,r); ctx.arcTo(x+w,y+h,x,y+h,r);
    ctx.arcTo(x,y+h,x,y,r); ctx.arcTo(x,y,x+w,y,r); ctx.closePath();
  }
  function ellipsePath(ctx,x,y,w,h){ ctx.beginPath(); ctx.ellipse(x+w/2,y+h/2,w/2,h/2,0,0,Math.PI*2); }

  /* ---------- holographic foil (Olografici) ---------- */
  // Pastel-metallic rainbow film: light silver base + soft diagonal bands
  // (cyan→violet→magenta→yellow→green), plus a radial/soft variation so it never looks
  // like a flat software gradient. `phase` gently shifts the bands for the live shimmer.
  function holoFoil(W,H,phase){
    phase=phase||0;
    var c=mkCanvas(W,H), x=c.getContext('2d');
    x.fillStyle='#d7dbe0'; x.fillRect(0,0,W,H);                 // silver base
    var off=phase*W*0.35;
    var g=x.createLinearGradient(-W*0.35+off, -H*0.15, W*1.15+off, H*1.15);
    var cols=['#a3e2ea','#b9abef','#f2b0dd','#f2e9bc','#b3e8c4','#a3e2ea','#b9abef','#f2b0dd','#f2e9bc'];
    cols.forEach(function(s,i){ g.addColorStop(i/(cols.length-1), s); });
    x.globalAlpha=0.98; x.fillStyle=g; x.fillRect(0,0,W,H); x.globalAlpha=1;
    // radial sheen + shadow to break the flatness
    var r=x.createRadialGradient(W*0.36,H*0.30,W*0.04, W*0.5,H*0.5,W*0.85);
    r.addColorStop(0,'rgba(255,255,255,.40)'); r.addColorStop(.45,'rgba(255,255,255,0)'); r.addColorStop(1,'rgba(120,128,148,.20)');
    x.fillStyle=r; x.fillRect(0,0,W,H);
    // a second, offset pastel pass (conic-ish via a rotated linear) for micro-variation
    var g2=x.createLinearGradient(W*1.1-off, 0, -W*0.1-off, H);
    ['rgba(150,230,215,.38)','rgba(210,185,240,.08)','rgba(248,190,215,.38)','rgba(225,235,165,.08)','rgba(165,215,240,.38)'].forEach(function(s,i,a){ g2.addColorStop(i/(a.length-1), s); });
    x.fillStyle=g2; x.fillRect(0,0,W,H);
    return c;
  }
  // Composite the printed DESIGN over the holo foil, clipped to the sticker outline `outerSil`.
  // multiply → white prints as full foil, black stays black, colours turn iridescent, and any
  // transparent area (die-cut margin, empty regions) shows silver foil.
  // realistic gold foil: warm base with high-contrast deep-gold ↔ champagne bands + specular sheen
  function goldFoil(W,H,phase,brushed){
    phase=phase||0;
    var c=mkCanvas(W,H), x=c.getContext('2d');
    x.fillStyle='#caa032'; x.fillRect(0,0,W,H);
    var off=phase*W*0.4;
    var g=x.createLinearGradient(-W*0.3+off,-H*0.2, W*1.1+off,H*1.2);
    ['#8a6a12','#f7e39c','#b8860b','#fff6d0','#9c7a1e','#f5d77a','#8a6a12','#fbeec0'].forEach(function(s,i,a){ g.addColorStop(i/(a.length-1),s); });
    x.fillStyle=g; x.fillRect(0,0,W,H);
    var r=x.createRadialGradient(W*0.35,H*0.3,W*0.05, W*0.5,H*0.5,W*0.85);
    r.addColorStop(0,'rgba(255,250,220,.55)'); r.addColorStop(.5,'rgba(255,255,255,0)'); r.addColorStop(1,'rgba(90,60,0,.30)');
    x.fillStyle=r; x.fillRect(0,0,W,H);
    if(!brushed){
      // stronger diagonal specular (glossy gold is more mirror-like than holo)
      var sp=x.createLinearGradient(0,0,W,H);
      sp.addColorStop(0,'rgba(255,255,255,0)'); sp.addColorStop(.46,'rgba(255,255,255,.55)');
      sp.addColorStop(.54,'rgba(255,255,255,.08)'); sp.addColorStop(1,'rgba(255,255,255,0)');
      x.fillStyle=sp; x.fillRect(0,0,W,H);
    } else {
      x.globalAlpha=0.10; x.strokeStyle='#3a2c00'; x.lineWidth=1;
      for(var y=0;y<H;y+=3){ x.beginPath(); x.moveTo(0,y+0.5); x.lineTo(W,y+0.5); x.stroke(); }
      x.globalAlpha=1;
    }
    return c;
  }
  function compositeHolo(o, outerSil, design, W, H, gold, brushed){
    var holo = gold ? goldFoil(W,H,0,brushed) : holoFoil(W,H);
    var comp=mkCanvas(W,H), cx=comp.getContext('2d');
    cx.drawImage(outerSil,0,0); cx.globalCompositeOperation='source-in'; cx.drawImage(holo,0,0);   // foil inside the shape
    cx.globalCompositeOperation='multiply'; cx.drawImage(design,0,0);                              // print × foil
    cx.globalCompositeOperation='destination-in'; cx.drawImage(outerSil,0,0);                      // reclip to the shape
    o.drawImage(comp,0,0);
  }

  // grow a white silhouette outward (die-cut border)
  function dilate(sil,r,color){
    var c=mkCanvas(sil.width,sil.height), x=c.getContext('2d'), i;
    var steps=Math.max(40, Math.ceil(r*2.5));   // più passi a raggio grande → niente scallop/bumps
    for(i=0;i<steps;i++){ var a=i/steps*Math.PI*2; x.drawImage(sil, Math.cos(a)*r, Math.sin(a)*r); }
    x.globalCompositeOperation='source-in'; x.fillStyle=color||'#fff'; x.fillRect(0,0,c.width,c.height);
    return c;
  }
  function rgbStr(c){ return 'rgb('+c[0]+','+c[1]+','+c[2]+')'; }
  // thin ring along the outer contour of a white silhouette (the faint die-cut line)
  function outlineRing(sil, thick, color){
    var inner = erode(sil, Math.max(1, thick));
    var c=mkCanvas(sil.width,sil.height), x=c.getContext('2d');
    x.drawImage(sil,0,0);
    x.globalCompositeOperation='destination-out'; x.drawImage(inner,0,0);
    x.globalCompositeOperation='source-in'; x.fillStyle=color||'rgba(70,74,82,.30)'; x.fillRect(0,0,c.width,c.height);
    return c;
  }
  // smooth a binary mask (round concave joins) via blur + threshold
  function smoothMask(mask, blur){
    var c=mkCanvas(mask.width,mask.height), x=c.getContext('2d');
    x.filter='blur('+blur+'px)'; x.drawImage(mask,0,0); x.filter='none';
    var id=x.getImageData(0,0,c.width,c.height), d=id.data;
    for(var i=3;i<d.length;i+=4){ d[i]= d[i]>110?255:0; }
    x.putImageData(id,0,0); return c;
  }
  // morphological erosion (intersection of translated copies)
  function erode(sil,r){
    var c=mkCanvas(sil.width,sil.height), x=c.getContext('2d');
    x.drawImage(sil,0,0); x.globalCompositeOperation='destination-in';
    var steps=28; for(var i=0;i<steps;i++){ var a=i/steps*Math.PI*2; x.drawImage(sil, Math.cos(a)*r, Math.sin(a)*r); }
    x.globalCompositeOperation='source-over'; return c;
  }
  // fill enclosed holes (only the outer contour matters for a die-cut piece)
  function fillHoles(sil){
    var W=sil.width,H=sil.height, x=sil.getContext('2d');
    var id=x.getImageData(0,0,W,H), d=id.data, outside=new Uint8Array(W*H), st=[];
    function seed(p){ if(!outside[p] && d[p*4+3]<128){ outside[p]=1; st.push(p); } }
    for(var i=0;i<W;i++){ seed(i); seed((H-1)*W+i); }
    for(var j=0;j<H;j++){ seed(j*W); seed(j*W+W-1); }
    while(st.length){ var p=st.pop(), px=p%W, py=(p-px)/W;
      if(px>0)seed(p-1); if(px<W-1)seed(p+1); if(py>0)seed(p-W); if(py<H-1)seed(p+W); }
    for(var q=0;q<W*H;q++){ if(d[q*4+3]<128 && !outside[q]){ d[q*4]=255; d[q*4+1]=255; d[q*4+2]=255; d[q*4+3]=255; } }
    x.putImageData(id,0,0); return sil;
  }
  // count connected white components (capped at 2 for an early-out)
  function components(sil){
    var W=sil.width,H=sil.height, d=sil.getContext('2d').getImageData(0,0,W,H).data;
    var vis=new Uint8Array(W*H), n=0, st=[];
    for(var s=0;s<W*H;s++){
      if(vis[s]||d[s*4+3]<128) continue; n++; if(n>1) return n;
      vis[s]=1; st.push(s);
      while(st.length){ var p=st.pop(), px=p%W, py=(p-px)/W;
        if(px>0){var l=p-1; if(!vis[l]&&d[l*4+3]>=128){vis[l]=1;st.push(l);}}
        if(px<W-1){var r=p+1; if(!vis[r]&&d[r*4+3]>=128){vis[r]=1;st.push(r);}}
        if(py>0){var u=p-W; if(!vis[u]&&d[u*4+3]>=128){vis[u]=1;st.push(u);}}
        if(py<H-1){var dn=p+W; if(!vis[dn]&&d[dn*4+3]>=128){vis[dn]=1;st.push(dn);}}
      }
    }
    return n;
  }
  // rounded bounding envelope (fallback when elements are too far to fuse cleanly)
  function roundedEnvelope(subSil,W,H){
    var d=subSil.getContext('2d').getImageData(0,0,W,H).data;
    var minx=W,miny=H,maxx=0,maxy=0,f=false;
    for(var y=0;y<H;y++)for(var x2=0;x2<W;x2++){ if(d[(y*W+x2)*4+3]>=128){ f=true; if(x2<minx)minx=x2; if(x2>maxx)maxx=x2; if(y<miny)miny=y; if(y>maxy)maxy=y; } }
    var c=mkCanvas(W,H), cx=c.getContext('2d'); if(!f) return subSil;
    var pad=Math.max(W,H)*0.05;
    var bx=Math.max(0,minx-pad), by=Math.max(0,miny-pad), bw=Math.min(W,maxx+pad)-bx, bh=Math.min(H,maxy+pad)-by;
    cx.fillStyle='#fff'; roundRectPath(cx,bx,by,bw,bh, Math.min(bw,bh)*0.16); cx.fill(); return c;
  }
  // white-area + bbox metrics for degeneration checks
  function silMetrics(sil){
    var W=sil.width,H=sil.height, d=sil.getContext('2d').getImageData(0,0,W,H).data;
    var n=0,minx=W,miny=H,maxx=0,maxy=0,f=false;
    for(var y=0;y<H;y++)for(var x=0;x<W;x++){ if(d[(y*W+x)*4+3]>=128){ n++; f=true; if(x<minx)minx=x; if(x>maxx)maxx=x; if(y<miny)miny=y; if(y>maxy)maxy=y; } }
    if(!f) return { area:0, bbox:1, ratio:0 };
    var ba=(maxx-minx+1)*(maxy-miny+1);
    return { area:n, bbox:ba, ratio:n/ba };
  }
  // merge all elements into ONE hole-free, organic die-cut silhouette (smallest closing radius).
  // SHARED by every material (trasparente, vinile, holo, oro): the material only changes what is
  // rendered UNDER the print and the margin finish — never the cut shape produced here.
  // label white components → centroids (+ pixel counts)
  function labelComps(cv){
    var W=cv.width,H=cv.height,d=cv.getContext('2d').getImageData(0,0,W,H).data;
    var lab=new Int32Array(W*H), comps=[], st=[];
    for(var s=0;s<W*H;s++){
      if(lab[s]||d[s*4+3]<128) continue;
      var id=comps.length+1; lab[s]=id; st.push(s); var sx=0,sy=0,cnt=0;
      while(st.length){ var p=st.pop(),px=p%W,py=(p-px)/W; sx+=px;sy+=py;cnt++;
        if(px>0){var l=p-1;if(!lab[l]&&d[l*4+3]>=128){lab[l]=id;st.push(l);}}
        if(px<W-1){var r=p+1;if(!lab[r]&&d[r*4+3]>=128){lab[r]=id;st.push(r);}}
        if(py>0){var u=p-W;if(!lab[u]&&d[u*4+3]>=128){lab[u]=id;st.push(u);}}
        if(py<H-1){var dn=p+W;if(!lab[dn]&&d[dn*4+3]>=128){lab[dn]=id;st.push(dn);}}
      }
      comps.push({cx:sx/cnt,cy:sy/cnt,count:cnt});
    }
    return comps;
  }
  // merge all elements into ONE hole-free, organic die-cut silhouette.
  // SHARED by every material (trasparente, vinile, holo, oro): the material only changes what is
  // rendered UNDER the print and the margin finish — never the cut shape produced here.
  // Profilo die-cut ADERENTE al soggetto (margine costante lo aggiunge poi dilate(refShape,borderW)).
  // NIENTE inflazione: la sagoma resta incollata alla stampa, così il bordo di 3 mm è uniforme ovunque.
  // Riempie solo i controni interni (buchi) e leviga appena i denti dell'anti-alias. Superset del
  // soggetto → non si svuota mai. La connessione in UN pezzo la fa il bordo (i margini adiacenti si
  // fondono) o, se serve, un ponte aggiunto DOPO la dilatazione nel ramo contour.
  function mergedSilhouette(subSil,W,H,maxSide){
    var c=mkCanvas(W,H); c.getContext('2d').drawImage(subSil,0,0);
    fillHoles(c);
    var res=smoothMask(c, Math.max(1, maxSide*0.004));   // micro de-jag, resta aderente
    res.getContext('2d').drawImage(subSil,0,0);          // superset del soggetto
    fillHoles(res);
    return res;
  }

  // fit a ratio box centered within a padded area
  function ratioBox(W,H,pad,ratio){
    var maxW=W-pad*2, maxH=H-pad*2, w=maxW, h=maxW/ratio;
    if(h>maxH){ h=maxH; w=maxH*ratio; }
    return { x:(W-w)/2, y:(H-h)/2, w:w, h:h };
  }

  /* ---------- finishes (clipped to body mask) ---------- */
  function finishOverlay(W,H,finitura,bodyMask,phase){
    var c=mkCanvas(W,H), x=c.getContext('2d');
    if(finitura==='olografica'){
      // subtle diagonal light band that sweeps across the foil (the shimmer accent).
      // soft-light keeps blacks black; the pastel rainbow itself is already baked into the base.
      var center=(-0.35 + 1.7*(phase||0));
      var g=x.createLinearGradient((center-0.26)*W, 0, (center+0.26)*W, H);
      g.addColorStop(0,'rgba(255,255,255,0)');
      g.addColorStop(.5,'rgba(255,255,255,.55)');
      g.addColorStop(1,'rgba(255,255,255,0)');
      x.fillStyle=g; x.fillRect(0,0,W,H);
      maskTo(x,bodyMask); c.__alpha=0.6; c.__blend='soft-light'; return c;
    }
    if(finitura==='laminato' || finitura==='uvgloss'){
      var gr=x.createLinearGradient(0,0,W,H);
      gr.addColorStop(0,'rgba(255,255,255,0)'); gr.addColorStop(.42,'rgba(255,255,255,.45)');
      gr.addColorStop(.52,'rgba(255,255,255,.05)'); gr.addColorStop(1,'rgba(255,255,255,0)');
      x.fillStyle=gr; x.fillRect(0,0,W,H);
      maskTo(x,bodyMask); c.__alpha=finitura==='uvgloss'?0.6:0.7; c.__blend='screen'; return c;
    }
    return null;
  }
  function maskTo(ctx,mask){ ctx.globalCompositeOperation='destination-in'; ctx.drawImage(mask,0,0); ctx.globalCompositeOperation='source-over'; }

  /* ---------- build ONE sticker (body + white border) into a canvas ---------- */
  // returns { canvas, sil } where sil is the white body silhouette (for finishes)
  function buildSticker(W,H,mode,ratio,pad,opts){
    opts=opts||{};
    var out=mkCanvas(W,H), o=out.getContext('2d');
    var box=ratioBox(W,H,pad,ratio);
    // white border in real millimetres (2 mm ≤5cm short side, else 3 mm), scaled to physical size
    var borderW = (opts.physW>0) ? borderPx(box, opts.physW, opts.physH) : (opts.borderW||Math.max(9,W*0.03));

    // ============================================================
    // RIGID BRANCHING — the two cut families never share a code path:
    //  · quadrato / tondo  → PURE geometric (below): no background removal, no subject
    //    mask, no single-piece fusion, no rounded-envelope fallback. Uses the ORIGINAL
    //    uploaded image (artOrig) drawn in cover, clipped to the shape. Instant, and
    //    immune to any cutout error.
    //  · sagomato / kiss-cut → contour pipeline (further down): mask → merge → dark-bg
    //    plate → fallback envelope → ever-present white die-cut border.
    // The fallback envelope (roundedEnvelope/mergedSilhouette) is reachable ONLY from the
    // contour branch — it is never invoked for geometric cuts.
    // ============================================================
    var geometric = (mode==='rect' || mode==='circle' || mode==='rounded');
    var transp = !!opts.trasparente;
    var olo = !!opts.olografico;
    var oro = !!opts.oro;

    // ---- contour (sagomato): single connected, hole-free die-cut piece; optional background plate ----
    if(!geometric && mode==='contour' && art && !fallback){
      // Die-cut: l'outline segue il SOGGETTO → inquadra il soggetto con un fill COSTANTE
      // (indipendente dal rapporto 5×3 / 5×5 / 3×3). Solo lo spessore del bordo (mm reali) scala
      // con la dimensione fisica. → 3×3 e 5×3 appaiono ugualmente ravvicinati, stesso margine.
      var frame=Math.round(Math.min(W,H)*0.74);
      var fpx=Math.round((W-frame)/2), fpy=Math.round((H-frame)/2);
      var cbox={ x:fpx, y:fpy, w:frame, h:frame };
      var artC=mkCanvas(W,H), ac=artC.getContext('2d'); drawSubjectContain(ac, art.canvas, artBBox, fpx, fpy, frame, frame);
      if(opts.physW>0 && opts.physH>0){
        var _maxPhys=Math.max(opts.physW,opts.physH), _shortCm=Math.min(opts.physW,opts.physH);
        borderW=Math.max(2, borderMM(_shortCm) * ((frame/_maxPhys)/10));
      }
      var subSil=mkCanvas(W,H), ss=subSil.getContext('2d'); ss.drawImage(artC,0,0); ss.globalCompositeOperation='source-in'; ss.fillStyle='#fff'; ss.fillRect(0,0,W,H);
      var shape=mergedSilhouette(subSil, W, H, frame);
      var plate = !!(opts.sfondoIncluso && bgRemovable && bgIsDark && bgColor);

      // The final die-cut outline = the SUBJECT silhouette (toggle OFF) OR a larger, soft dark PLATE
      // that contains the subject (toggle ON). Border/shadow follow THIS shape.
      var refShape = shape;
      if(plate){
        var pr = frame*0.10;
        var grown = dilate(shape, pr, '#fff');
        fillHoles(grown);
        refShape = smoothMask(grown, Math.max(2, pr*0.45));
      }
      var outer = dilate(refShape, borderW, '#fff');   // shape + margine COSTANTE (die-cut outline)
      fillHoles(outer);                                // chiudi le tasche tra bordi adiacenti
      // se restano vuoti/pezzi separati (elementi troppo distanti) AUMENTA il margine (4/5/6 mm…)
      // finché tutto è un pezzo unico: il bordo resta COSTANTE, solo più largo. Niente ponti sottili.
      (function(){
        var mmPx = (opts.physW>0 && opts.physH>0) ? (frame/Math.max(opts.physW,opts.physH))/10 : borderW/2;
        var grow = borderW, guard = 0;
        while(labelComps(outer).length>1 && guard<12){
          grow += mmPx;                                 // +1 mm per passo (3→4→5→6…)
          outer = dilate(refShape, grow, '#fff');
          fillHoles(outer);
          guard++;
        }
        borderW = grow;                                 // usa lo stesso margine per ombra/plate
        // ANTI-ALIAS leggero del profilo (mantiene fedele il contorno + margine 3 mm), niente sfocatura
        var aa=mkCanvas(W,H), ax=aa.getContext('2d');
        ax.filter='blur(1px)'; ax.drawImage(outer,0,0); ax.filter='none';
        var aid=ax.getImageData(0,0,W,H), ad=aid.data;
        for(var fi=3;fi<ad.length;fi+=4){ ad[fi]= ad[fi]>90?255:0; }   // soglia bassa: margine pieno, bordo netto
        ax.putImageData(aid,0,0);
        var bb=mkCanvas(W,H), bx=bb.getContext('2d');
        bx.filter='blur(0.5px)'; bx.drawImage(aa,0,0); bx.filter='none';  // micro-antialias
        outer=bb;
      })();

      if(transp){
        // shadow as a soft HALO only (no opaque body) — cast, then knock the body back out
        var shc=mkCanvas(W,H), scx=shc.getContext('2d');
        scx.shadowColor='rgba(20,16,16,.16)'; scx.shadowBlur=W*0.03; scx.shadowOffsetY=W*0.014;
        scx.drawImage(outer,0,0);
        scx.shadowColor='transparent'; scx.globalCompositeOperation='destination-out'; scx.drawImage(outer,0,0);
        o.drawImage(shc,0,0);
        // clear-film margin: faint white haze through which the context bg shows
        o.save(); o.globalAlpha=0.10; o.drawImage(outer,0,0); o.restore();
        // print layer, then punch the white (non-printed) pixels so the film shows through them
        var pl=mkCanvas(W,H), plc=pl.getContext('2d');
        if(plate){
          // dark/colored plate over the whole die-cut shape …
          plc.drawImage(refShape,0,0); plc.globalCompositeOperation='source-in'; plc.fillStyle=rgbStr(bgColor); plc.fillRect(0,0,W,H);
          plc.globalCompositeOperation='source-over';
          // … real subject colours on top (clipped to the shape) …
          var subj=mkCanvas(W,H), sjx=subj.getContext('2d'); sjx.drawImage(refShape,0,0); sjx.globalCompositeOperation='source-in';
          drawSubjectContain(sjx, artOrig||art.canvas, artBBox, fpx, fpy, frame, frame);
          plc.drawImage(subj,0,0);
          // … then punch the graphic's white pixels ONLY for real-transparency files (alpha PNG/PDF);
          // an opaque file (white subject on a coloured bg) prints as-is.
          if(hasAlpha){
            var wm=mkCanvas(W,H), wmx=wm.getContext('2d'); drawSubjectContain(wmx, whiteMaskOf(artOrig||art.canvas), artBBox, fpx, fpy, frame, frame);
            plc.globalCompositeOperation='destination-out'; plc.drawImage(wm,0,0);
          }
        } else {
          // no plate: only the cut-out subject, white → transparent
          plc.drawImage(refShape,0,0); plc.globalCompositeOperation='source-in';
          drawSubjectContain(plc, transpSubject()||art.canvas, artBBox, fpx, fpy, frame, frame);
        }
        o.save(); o.globalAlpha=0.9; o.drawImage(pl,0,0); o.restore();   // coloured print at ~90% coverage
        o.drawImage(outlineRing(outer, Math.max(1.2, borderW*0.14), 'rgba(70,74,82,.32)'), 0, 0);
        return { canvas:out, sil:outer, box:cbox };
      }

      // opaque materials: shadow + solid white base + border in one piece
      o.save();
      o.shadowColor='rgba(20,16,16,.30)'; o.shadowBlur=W*0.045; o.shadowOffsetY=W*0.028;
      o.drawImage(dilate(refShape, borderW, '#000'), 0, 0);
      o.restore();

      if(olo){
        // holo foil: silver margin + design multiplied over the rainbow film
        var dO=mkCanvas(W,H), dOx=dO.getContext('2d');
        if(plate){ dOx.drawImage(refShape,0,0); dOx.globalCompositeOperation='source-in'; dOx.fillStyle=rgbStr(bgColor); dOx.fillRect(0,0,W,H); dOx.globalCompositeOperation='source-over'; }
        drawSubjectContain(dOx, art.canvas, artBBox, fpx, fpy, frame, frame);
        compositeHolo(o, outer, dO, W, H, oro);
        return { canvas:out, sil:outer, box:cbox };
      }

      o.drawImage(outer,0,0);
      if(plate){
        var pc=mkCanvas(W,H), pcx=pc.getContext('2d'); pcx.drawImage(refShape,0,0); pcx.globalCompositeOperation='source-in'; pcx.fillStyle=rgbStr(bgColor); pcx.fillRect(0,0,W,H);
        o.drawImage(pc,0,0);
      }
      var cb=mkCanvas(W,H), cyb=cb.getContext('2d'); cyb.drawImage(refShape,0,0); cyb.globalCompositeOperation='source-in';
      drawSubjectContain(cyb, art.canvas, artBBox, fpx, fpy, frame, frame);
      o.drawImage(cb,0,0);
      return { canvas:out, sil:refShape, box:cbox };
    }

    // ---- geometric (quadrato / tondo): the FULL artwork, cover-cropped, clipped to the cut shape ----
    // Uses the original image (background included) — a rectangular/round cut keeps the whole graphic,
    // it does NOT follow the subject. drawCover is shared by both toggle branches; ON only adds the
    // white plate + a real-millimetre inset.
    var geo = (mode==='circle') ? 'circle' : 'rect';
    var radF = (mode==='rounded') ? 0.22 : 0;   // rounded-corners cut vs SQUARE (sharp 90° corners)
    // Graphic WITHOUT background (native alpha or a removed uniform bg): center it (contain) inside
    // the shape at the chosen size and let the shape/film show through the empty areas — like the
    // sagomato border. A full-bleed opaque image still cover-fills the shape.
    // Trim to subject ONLY for a graphic without design background: native alpha, or a removed
    // uniform bg that is WHITE/technical. A colored or dark uniform bg is part of the design →
    // keep it full-bleed (cover), unless the user opts in with "Rimuovi lo sfondo".
    var noBg = hasAlpha || (bgRemovable && (!bgIsDark || opts.geoRemoveBg));
    var artSource = noBg ? (art ? art.canvas : artOrig) : artOrig;
    // Full-bleed opaque image → cover-crop to the die line. Trimmed subject (alpha / removed bg) →
    // contain inside a 5mm-in-scale safety margin, pixel-centered; a circle inscribes in the reduced ellipse.
    function placeArt(ctx, src, x, y, w, h){
      if(!noBg){ drawCover(ctx, src, x, y, w, h); return; }
      var m = safeMarginPx(box, opts.physW);
      var ix=x+m, iy=y+m, iw=Math.max(1,w-2*m), ih=Math.max(1,h-2*m);
      if(geo==='circle'){ var cw=iw/Math.SQRT2, ch=ih/Math.SQRT2; ix=x+w/2-cw/2; iy=y+h/2-ch/2; iw=cw; ih=ch; }
      drawSubjectContain(ctx, src, artBBox, ix, iy, iw, ih);
    }
    function shapePathTo(ctx, bx, by, bw, bh){
      if(geo==='circle') ellipsePath(ctx, bx, by, bw, bh);
      else roundRectPath(ctx, bx, by, bw, bh, Math.min(bw,bh)*radF);   // 22% rounded · 8% square
    }
    // white silhouette of the cut shape — clip mask + shadow caster (and, when ON, the plate)
    var sil2=mkCanvas(W,H), s2=sil2.getContext('2d'); s2.fillStyle='#fff'; shapePathTo(s2, box.x, box.y, box.w, box.h); s2.fill();
    if(transp){
      // Trasparente: NO opaque body — cast a soft shadow halo only, then knock the body back out
      var shg=mkCanvas(W,H), shx=shg.getContext('2d');
      shx.shadowColor='rgba(20,16,16,.15)'; shx.shadowBlur=W*0.028; shx.shadowOffsetY=W*0.014;
      shx.drawImage(sil2,0,0);
      shx.shadowColor='transparent'; shx.globalCompositeOperation='destination-out'; shx.drawImage(sil2,0,0);
      o.drawImage(shg,0,0);
    } else {
      o.save();
      o.shadowColor='rgba(20,16,16,.30)'; o.shadowBlur=W*0.045; o.shadowOffsetY=W*0.028;
      o.drawImage(sil2,0,0); o.restore();   // opaque materials: white substrate + shadow
    }

    if(transp){
      // Trasparente: no white plate ever. Clear-film substrate (faint haze) + honest-colour artwork
      // cover-cropped, plus a thin cut line. White areas of the graphic stay clear (context bg shows).
      o.save(); o.globalAlpha=0.10; o.drawImage(sil2,0,0); o.restore();
      o.save(); shapePathTo(o, box.x, box.y, box.w, box.h); o.clip();
      var srcT = noBg ? (transpSubject() || transpFull()) : transpFull();
      if(srcT) placeArt(o, srcT, box.x, box.y, box.w, box.h);
      o.restore();
      o.drawImage(outlineRing(sil2, Math.max(1.2, borderW*0.14), 'rgba(70,74,82,.32)'), 0, 0);
      return { canvas:out, sil:sil2, box:box };
    }

    if(olo){
      // holo foil geometric: design multiplied over foil; "Bordo holo" (opts.bordoBianco) insets it
      var ibO = box;
      if(opts.bordoBianco){ var ins=borderW; ibO={ x:box.x+ins, y:box.y+ins, w:Math.max(1,box.w-2*ins), h:Math.max(1,box.h-2*ins) }; }
      var dG=mkCanvas(W,H), dGx=dG.getContext('2d');
      dGx.save(); shapePathTo(dGx, ibO.x, ibO.y, ibO.w, ibO.h); dGx.clip();
      if(artSource) placeArt(dGx, artSource, ibO.x, ibO.y, ibO.w, ibO.h);
      dGx.restore();
      compositeHolo(o, sil2, dG, W, H, oro);
      return { canvas:out, sil:sil2, box:box };
    }

    if(opts.bordoBianco){
      // ON: white plate over the whole shape, then artwork cover-cropped inside a shape inset by the
      // adaptive border (2 or 3 mm). insetPx == borderW; e.g. 10×7 → 3/70 ≈ 4.3% of the short side.
      o.drawImage(sil2,0,0);
      var inset = borderW;
      var ib = { x:box.x+inset, y:box.y+inset, w:Math.max(1,box.w-2*inset), h:Math.max(1,box.h-2*inset) };
      o.save(); shapePathTo(o, ib.x, ib.y, ib.w, ib.h); o.clip();
      if(artSource) placeArt(o, artSource, ib.x, ib.y, ib.w, ib.h);
      o.restore();
    } else {
      // OFF (default): artwork cover-cropped, clipped to the cut shape — no inset, no white margin.
      // The opaque vinyl substrate is fully covered by an opaque graphic; white shows only through the
      // graphic's own transparency, never as a margin.
      o.drawImage(sil2,0,0);
      o.save(); shapePathTo(o, box.x, box.y, box.w, box.h); o.clip();
      if(artSource) placeArt(o, artSource, box.x, box.y, box.w, box.h);
      o.restore();
    }
    return { canvas:out, sil:sil2, box:box };
  }

  /* ---------- DTF transfer film (Termoadesivi) ---------- */
  // A rectangular frosted transport film with the artwork printed on top at full opacity
  // (DTF white ink is opaque → colours stay true even on dark fabric). No sticker rules apply:
  // no border, no die-cut, no single-piece fusion, no plate. Separate elements are normal.
  function buildDTF(W,H,ratio,removeBg){
    var out=mkCanvas(W,H), o=out.getContext('2d');
    var box=ratioBox(W,H,W*0.10,ratio);
    var rad=Math.min(box.w,box.h)*0.03;
    // soft sheet shadow
    o.save(); o.shadowColor='rgba(20,16,16,.16)'; o.shadowBlur=W*0.03; o.shadowOffsetY=W*0.014;
    roundRectPath(o, box.x,box.y,box.w,box.h, rad); o.fillStyle='rgba(240,244,248,0.10)'; o.fill(); o.restore();
    // frosted film body + satin sheen (semi-transparent → fabric shows through non-printed areas)
    o.save(); roundRectPath(o, box.x,box.y,box.w,box.h, rad); o.clip();
    o.fillStyle='rgba(244,247,251,0.13)'; o.fillRect(box.x,box.y,box.w,box.h);
    var g=o.createLinearGradient(box.x,box.y,box.x+box.w,box.y+box.h);
    g.addColorStop(0,'rgba(255,255,255,.16)'); g.addColorStop(.42,'rgba(255,255,255,.03)');
    g.addColorStop(.55,'rgba(255,255,255,.11)'); g.addColorStop(1,'rgba(255,255,255,.02)');
    o.fillStyle=g; o.fillRect(box.x,box.y,box.w,box.h);
    // artwork printed on top, FULL opacity, contained with a small inner margin
    var src = (removeBg && bgRemovable && art) ? art.canvas : artOrig;
    var m=Math.min(box.w,box.h)*0.07;
    if(src) drawContain(o, src, box.x+m, box.y+m, box.w-2*m, box.h-2*m);
    o.restore();
    // faint film edge
    roundRectPath(o, box.x,box.y,box.w,box.h, rad); o.strokeStyle='rgba(120,132,148,.30)'; o.lineWidth=1.2; o.stroke();
    // print-only layer for production export (artwork on transparency, no film scenery)
    var print=mkCanvas(W,H), px=print.getContext('2d');
    if(src){ var m2=Math.min(box.w,box.h)*0.07; drawContain(px, src, box.x+m2, box.y+m2, box.w-2*m2, box.h-2*m2); }
    var sil=mkCanvas(W,H), sx=sil.getContext('2d'); sx.fillStyle='#fff'; roundRectPath(sx,box.x,box.y,box.w,box.h,rad); sx.fill();
    return { canvas:out, sil:sil, box:box, printOnly:print };
  }

  /* ---------- Prespaziati (vinile intagliato) ---------- */
  // Monochrome cut-vinyl silhouette: the subject mask filled entirely with the chosen vinyl colour.
  // Internal holes are PRESERVED (counters of letters show the background) — no fill-holes, no single-
  // piece fusion, no border/plate. Separate elements are normal.
  function maskBBoxPx(cv){
    var W=cv.width,H=cv.height, d=cv.getContext('2d').getImageData(0,0,W,H).data;
    var minx=W,miny=H,maxx=0,maxy=0,f=false;
    for(var y=0;y<H;y++)for(var x=0;x<W;x++){ if(d[(y*W+x)*4+3]>24){ f=true; if(x<minx)minx=x; if(x>maxx)maxx=x; if(y<miny)miny=y; if(y>maxy)maxy=y; } }
    return f?{x:minx,y:miny,w:maxx-minx+1,h:maxy-miny+1}:null;
  }
  function buildVinyl(W,H,ratio,col,tape){
    var out=mkCanvas(W,H), o=out.getContext('2d');
    var box=ratioBox(W,H,W*0.15,ratio);
    var src = vinylSubject();
    if(!src) return { canvas:out, sil:null, box:box, printOnly:mkCanvas(W,H) };
    // subject placed (contained) at the chosen size — keeps the subject's own alpha (holes included)
    var artC=mkCanvas(W,H), ac=artC.getContext('2d'); drawContain(ac, src, box.x, box.y, box.w, box.h);
    // soft shadow of the cut vinyl
    o.save(); o.shadowColor='rgba(20,16,16,.22)'; o.shadowBlur=W*0.02; o.shadowOffsetY=W*0.012; o.drawImage(artC,0,0); o.restore();
    // fill the mask with the vinyl colour (flat, or a metallic gradient for oro/argento)
    col = col || { hex:'#111' };
    var fill=mkCanvas(W,H), fx=fill.getContext('2d'); fx.drawImage(artC,0,0); fx.globalCompositeOperation='source-in';
    if(col.metal){
      var lo=col.hex2 || '#ffffff';
      var g=fx.createLinearGradient(box.x, box.y, box.x+box.w, box.y+box.h);
      g.addColorStop(0,lo); g.addColorStop(.28,col.hex); g.addColorStop(.5,lo); g.addColorStop(.72,col.hex); g.addColorStop(1,lo);
      fx.fillStyle=g;
    } else { fx.fillStyle=col.hex; }
    fx.fillRect(0,0,W,H);
    o.drawImage(fill,0,0);
    // optional application-tape overlay (how the product is delivered)
    if(tape){
      var bb=maskBBoxPx(artC);
      if(bb){ var pad=W*0.025;
        var tx=bb.x-pad, ty=bb.y-pad, tw=bb.w+2*pad, th=bb.h+2*pad;
        o.save(); o.globalAlpha=0.16; o.fillStyle='#e3e9f0'; o.fillRect(tx,ty,tw,th); o.restore();
        o.save(); o.globalAlpha=0.5; var tg=o.createLinearGradient(tx,0,tx+tw,0);
        tg.addColorStop(0,'rgba(255,255,255,.5)'); tg.addColorStop(.5,'rgba(255,255,255,0)'); tg.addColorStop(1,'rgba(255,255,255,.35)');
        o.fillStyle=tg; o.fillRect(tx,ty,tw,th); o.restore();
        o.strokeStyle='rgba(110,130,150,.45)'; o.lineWidth=1; o.strokeRect(tx+0.5,ty+0.5,tw-1,th-1);
      }
    }
    return { canvas:out, sil:fill, box:box, printOnly:fill };
  }

  /* ---------- Stampa-Taglio (die-cut multicolore, INTAGLIATI) ---------- */
  // OPPOSTO ai Sagomati: nessuna fusione in pezzo unico, nessuna chiusura morfologica,
  // nessun riempimento dei buchi. La grafica è mostrata a COLORI REALI e la lama segue il
  // profilo di OGNI elemento (lettera per lettera), con i vuoti interni ritagliati. Gli
  // elementi separati restano separati, ciascuno con la propria ombra; opzione bordo per pezzo;
  // pellicola di trasporto (application tape) opzionale.
  function buildStampaTaglio(W,H,opts){
    opts=opts||{};
    var out=mkCanvas(W,H), o=out.getContext('2d');
    var frame=Math.round(Math.min(W,H)*0.76);
    var fx=Math.round((W-frame)/2), fy=Math.round((H-frame)/2);
    var cbox={ x:fx, y:fy, w:frame, h:frame };
    // sorgente a colori con i vuoti/gap trasparenti: usa la rimozione per SIMILARITÀ di colore
    // (come i prespaziati), così i contro-occhielli racchiusi (buchi di O/A/e, fori dei loghi)
    // restano VUOTI e non solo lo sfondo connesso al bordo. Colori reali preservati.
    var colorSrc = (art && !fallback) ? (vinylSubject() || art.canvas) : artOrig;
    if(!colorSrc) return { canvas:out, sil:null, box:cbox, printOnly:mkCanvas(W,H) };

    // grafica a colori pieni, inquadrata a fill costante (i buchi restano trasparenti)
    var artC=mkCanvas(W,H), ac=artC.getContext('2d');
    drawSubjectContain(ac, colorSrc, artBBox, fx, fy, frame, frame);

    // silhouette per-elemento (ogni blob + i suoi buchi) — alpha della grafica riempita di bianco
    var silW=mkCanvas(W,H), sw=silW.getContext('2d'); sw.drawImage(artC,0,0);
    sw.globalCompositeOperation='source-in'; sw.fillStyle='#fff'; sw.fillRect(0,0,W,H);

    // bordo opzionale in mm reali attorno a OGNI elemento (dilate cresce ogni blob singolarmente;
    // i buchi si restringono ma restano → il bordo segue anche il profilo interno)
    var borderLayer=null, borderW=0;
    if(opts.border && opts.physW>0 && opts.physH>0){
      var maxPhys=Math.max(opts.physW,opts.physH), shortCm=Math.min(opts.physW,opts.physH);
      borderW=Math.max(2, borderMM(shortCm) * ((frame/maxPhys)/10));
      borderLayer=dilate(silW, borderW, opts.borderColor||'#ffffff');
    }

    var caster = borderLayer || artC;   // chi proietta l'ombra (per-elemento: canvas shadow segue ogni blob)
    o.save();
    o.shadowColor='rgba(20,16,16,.30)'; o.shadowBlur=W*0.02; o.shadowOffsetY=W*0.012;
    o.drawImage(caster,0,0);
    o.restore();
    // con bordo: l'ombra ha già disegnato il bordo colorato → sopra vanno i colori reali.
    // senza bordo: l'ombra ha già disegnato la grafica a colori.
    if(borderLayer){ o.drawImage(artC,0,0); }

    // pellicola di trasporto (application tape): velo semi-trasparente sull'ingombro complessivo
    if(opts.tape){
      var bb=maskBBoxPx(borderLayer||artC);
      if(bb){ var pad=W*0.02;
        var tx=bb.x-pad, ty=bb.y-pad, tw=bb.w+2*pad, th=bb.h+2*pad;
        o.save(); o.globalAlpha=0.16; o.fillStyle='#e3e9f0'; o.fillRect(tx,ty,tw,th); o.restore();
        o.save(); o.globalAlpha=0.5; var tg=o.createLinearGradient(tx,0,tx+tw,0);
        tg.addColorStop(0,'rgba(255,255,255,.5)'); tg.addColorStop(.5,'rgba(255,255,255,0)'); tg.addColorStop(1,'rgba(255,255,255,.35)');
        o.fillStyle=tg; o.fillRect(tx,ty,tw,th); o.restore();
        o.strokeStyle='rgba(110,130,150,.45)'; o.lineWidth=1; o.strokeRect(tx+0.5,ty+0.5,tw-1,th-1);
      }
    }

    // export: colori reali (+ bordo se attivo) su fondo trasparente
    var print=mkCanvas(W,H), px=print.getContext('2d');
    if(borderLayer) px.drawImage(borderLayer,0,0);
    px.drawImage(artC,0,0);

    return { canvas:out, sil:(borderLayer||silW), box:cbox, printOnly:print };
  }

  /* ---------- Mezzo Taglio (kiss-cut) ---------- */
  // La lama incide SOLO il vinile, non il supporto. Tre livelli: 1) backing rettangolare di carta
  // siliconata con margine 2 cm reali attorno al taglio; 2) vinile con bleed 2 mm (edge-extend dei
  // colori di bordo); 3) linea di taglio incisa sul profilo ORIGINALE (non passante).
  // Espande i colori di bordo verso l'esterno (ogni copia offset porta i propri colori locali)
  function edgeExtend(src, r){
    var c=mkCanvas(src.width, src.height), x=c.getContext('2d');
    var steps=Math.max(5, Math.round(r)), ring=28;
    for(var d=steps; d>=1; d--){
      var rr=(d/steps)*r;
      for(var i=0;i<ring;i++){ var a=i/ring*Math.PI*2; x.drawImage(src, Math.cos(a)*rr, Math.sin(a)*rr); }
    }
    x.drawImage(src,0,0);   // grafica piena sopra
    return c;
  }
  // solco inciso: linea scura + filo di luce lungo il profilo (suggerisce l'incisione nel vinile)
  function grooveLine(sil, t){
    var c=mkCanvas(sil.width, sil.height), x=c.getContext('2d');
    x.drawImage(outlineRing(sil, Math.max(1.4,t), 'rgba(20,16,16,.30)'), 0, 0);
    x.drawImage(outlineRing(sil, Math.max(0.8,t*0.45), 'rgba(255,255,255,.55)'), 0, 0);
    return c;
  }
  function buildKissCut(W,H,mode,opts){
    opts=opts||{};
    var out=mkCanvas(W,H), o=out.getContext('2d');
    if(!art && !artOrig) return { canvas:out, sil:null, box:{x:0,y:0,w:W,h:H}, printOnly:mkCanvas(W,H) };
    var physW=opts.physW||5, physH=opts.physH||5;

    // fit-to-frame: il BACKING (livello più esterno) riempie il frame costante
    var frameMax=Math.round(Math.min(W,H)*0.86);
    var cutLongCm=Math.max(physW,physH);
    var marginPx=frameMax*0.8/(cutLongCm+1.6);             // 0.8 cm di supporto per lato, in scala
    var pxPerCm=(frameMax-2*marginPx)/cutLongCm;
    var bleedPx=Math.max(2, 0.2*pxPerCm);                  // 2 mm di abbondanza
    var cutW=physW*pxPerCm, cutH=physH*pxPerCm;
    var cx=(W-cutW)/2, cy=(H-cutH)/2;
    var geometric=(mode==='rect'||mode==='circle'||mode==='rounded');
    var noBg = hasAlpha || (bgRemovable && (!bgIsDark || opts.geoRemoveBg));

    var sil=mkCanvas(W,H), sc=sil.getContext('2d');        // silhouette linea di taglio (bianca)
    var gfx=mkCanvas(W,H), gc=gfx.getContext('2d');        // vinile: base bianca + grafica

    if(geometric){
      var radF=(mode==='rounded')?0.22:0;
      var shape=function(ctx,x,y,w,h){ if(mode==='circle') ellipsePath(ctx,x,y,w,h); else roundRectPath(ctx,x,y,w,h,Math.min(w,h)*radF); };
      sc.fillStyle='#fff'; shape(sc,cx,cy,cutW,cutH); sc.fill();
      gc.drawImage(sil,0,0);                                // base vinile bianca
      gc.save(); shape(gc,cx,cy,cutW,cutH); gc.clip();
      var gsrc = noBg ? (art?art.canvas:artOrig) : artOrig;
      if(noBg){
        var m=Math.min(cutW,cutH)*0.05;
        if(mode==='circle'){ var iw=(cutW-2*m)/Math.SQRT2, ih=(cutH-2*m)/Math.SQRT2; drawSubjectContain(gc,gsrc,artBBox,cx+cutW/2-iw/2,cy+cutH/2-ih/2,iw,ih); }
        else drawSubjectContain(gc,gsrc,artBBox,cx+m,cy+m,cutW-2*m,cutH-2*m);
      } else { drawCover(gc,gsrc,cx,cy,cutW,cutH); }
      gc.restore();
    } else {
      // sagomato mezzo taglio: SAGOMA UNICA PIENA con bordo costante 3 mm adattivo (stessa logica del die-cut).
      // Regola binaria AUTOMATICA (nessun toggle):
      //  · DEFAULT → bordo BIANCO uniforme attorno alla sagoma del soggetto (multicolore, monocolore,
      //    dettagli fini, con o senza trasparenza). Vuoti interni chiusi dall'espansione adattiva.
      //  · ECCEZIONE — sfondo piatto a tinta unica (colore = parte del design, es. magenta col galletto
      //    bianco): il taglio segue la sagoma ESTERNA della composizione (sfondo incluso) e il margine
      //    è il colore di sfondo stesso, NON bianco. Il colore arriva pieno fino alla linea di taglio.
      var flatBg = !!(bgRemovable && bgIsDark && bgColor);
      var gsrc = flatBg ? artOrig : (art?art.canvas:artOrig);
      var srcBox = flatBg ? {x:0,y:0,w:1,h:1} : artBBox;   // eccezione: intera composizione, sfondo compreso
      var artC=mkCanvas(W,H), acx=artC.getContext('2d'); drawSubjectContain(acx, gsrc, srcBox, cx, cy, cutW, cutH);
      var subSil=mkCanvas(W,H), ss=subSil.getContext('2d'); ss.drawImage(artC,0,0); ss.globalCompositeOperation='source-in'; ss.fillStyle='#fff'; ss.fillRect(0,0,W,H);
      var merged=mergedSilhouette(subSil, W, H, Math.max(cutW,cutH));

      // bordo costante in mm reali + crescita adattiva finché la sagoma è UN pezzo unico senza vuoti interni
      var mmPx=pxPerCm/10, shortCm=Math.min(physW,physH);
      var kBorderW=Math.max(2, borderMM(shortCm)*mmPx);        // 3 mm base (2 mm se lato corto ≤ 5 cm)
      var cutSil=dilate(merged,kBorderW,'#fff'); fillHoles(cutSil);
      var grow=kBorderW, guard=0;
      while(labelComps(cutSil).length>1 && guard<12){ grow+=mmPx; cutSil=dilate(merged,grow,'#fff'); fillHoles(cutSil); guard++; }  // 3→4→5→6 mm…
      cutSil=smoothMask(cutSil, Math.max(2,kBorderW*0.4)); fillHoles(cutSil);
      sc.clearRect(0,0,W,H); sc.drawImage(cutSil,0,0);         // linea di taglio = sagoma unica piena

      kissBorderMode = flatBg ? 'flat' : 'white';
      gc.drawImage(cutSil,0,0);                                // base vinile bianca = intera sagoma
      if(flatBg){
        // ECCEZIONE: colore di sfondo pieno fino al taglio (margine = sfondo, nessun bordo bianco aggiunto)
        var spreadG=edgeExtend(artC, grow+bleedPx);
        var clip=mkCanvas(W,H), cc=clip.getContext('2d'); cc.drawImage(spreadG,0,0); cc.globalCompositeOperation='destination-in'; cc.drawImage(cutSil,0,0);
        gc.drawImage(clip,0,0);
      } else {
        // DEFAULT: BORDO BIANCO — colori reali solo dove c'è grafica; margine e vuoti chiusi restano bianchi
        var cbn=mkCanvas(W,H), cbx=cbn.getContext('2d'); cbx.drawImage(cutSil,0,0); cbx.globalCompositeOperation='source-in'; cbx.drawImage(artC,0,0);
        gc.drawImage(cbn,0,0);
      }
    }

    // bleed: colori estesi 2 mm oltre la linea, sotto la grafica
    var bleedShape=dilate(sil, bleedPx, '#fff');
    var spread=edgeExtend(gfx, bleedPx);
    var bleedLayer=mkCanvas(W,H), bl=bleedLayer.getContext('2d'); bl.drawImage(spread,0,0); bl.globalCompositeOperation='destination-in'; bl.drawImage(bleedShape,0,0);

    // backing rettangolare = bbox del taglio + margine 0.8 cm, carta siliconata con ombra propria
    var bb=maskBBoxPx(sil) || {x:cx,y:cy,w:cutW,h:cutH};
    var rx=bb.x-marginPx, ry=bb.y-marginPx, rw=bb.w+2*marginPx, rh=bb.h+2*marginPx;
    var rrad=Math.min(rw,rh)*0.045;
    o.save(); o.shadowColor='rgba(20,16,16,.26)'; o.shadowBlur=W*0.03; o.shadowOffsetY=W*0.015;
    roundRectPath(o,rx,ry,rw,rh,rrad); o.fillStyle='#f5f1e8'; o.fill(); o.restore();       // avorio opaco
    o.save(); roundRectPath(o,rx,ry,rw,rh,rrad); o.clip();
    var pg=o.createLinearGradient(rx,ry,rx+rw,ry+rh);
    pg.addColorStop(0,'rgba(255,255,255,.55)'); pg.addColorStop(.5,'rgba(255,255,255,0)'); pg.addColorStop(1,'rgba(0,0,0,.04)');
    o.fillStyle=pg; o.fillRect(rx,ry,rw,rh); o.restore();
    roundRectPath(o,rx,ry,rw,rh,rrad); o.strokeStyle='rgba(120,110,88,.30)'; o.lineWidth=1; o.stroke();

    // vinile: bleed sotto, grafica sopra, poi il solco di taglio inciso sul profilo originale
    o.drawImage(bleedLayer,0,0);
    o.drawImage(gfx,0,0);
    o.drawImage(grooveLine(sil, Math.max(1.4, bleedPx*0.5)), 0, 0);

    var print=mkCanvas(W,H), px=print.getContext('2d'); px.drawImage(bleedLayer,0,0); px.drawImage(gfx,0,0);
    return { canvas:out, sil:sil, box:{x:rx,y:ry,w:rw,h:rh}, printOnly:print };
  }

  // number of distinct saturated colours in the subject (multicolour → not cut-vinyl friendly)
  function subjectColorCount(){
    if(!artOrig || fallback) return 1;
    var w=artOrig.width, h=artOrig.height, d=artOrig.getContext('2d').getImageData(0,0,w,h).data;
    var buckets={}, tot=0;
    for(var i=0;i<d.length;i+=4){ if(d[i+3]<128) continue;
      var r=d[i],g=d[i+1],b=d[i+2];
      var mx=Math.max(r,g,b), mn=Math.min(r,g,b), sat=mx-mn, lum=(r+g+b)/3;
      if(lum>238 || lum<16 || sat<28) continue;            // skip white/black/greys
      var key = Math.round(r/64)+'-'+Math.round(g/64)+'-'+Math.round(b/64);
      buckets[key]=(buckets[key]||0)+1; tot++;
    }
    if(!tot) return 1;
    var n=0; for(var k in buckets){ if(buckets[k] > tot*0.04) n++; }   // ≥4% of coloured pixels
    return Math.max(1,n);
  }

  // estimate the thinnest stroke of the mask, in real millimetres, at the chosen physical size
  function minDetailMm(physLongCm){
    if(!art || fallback || !artBBox) return 99;
    var W=art.w, H=art.h;
    var m=mkCanvas(W,H), mx=m.getContext('2d'); mx.drawImage(art.canvas,0,0); mx.globalCompositeOperation='source-in'; mx.fillStyle='#fff'; mx.fillRect(0,0,W,H);
    var base=silMetrics(m).area; if(!base) return 99;
    var subjLongPx = Math.max(artBBox.w*W, artBBox.h*H) || Math.max(W,H);
    var mmPerPx = (physLongCm*10) / subjLongPx;            // real mm per art-pixel
    for(var r=1; r<=14; r++){
      if(silMetrics(erode(m, r)).area < base*0.5){ return (2*r) * mmPerPx; }
    }
    return (2*15) * mmPerPx;
  }

  /* ---------- kiss-cut sheet: grid of die-cut copies with dashed outline ---------- */
  function buildSheet(W,H,ratio,sfondoIncluso,physW,physH,trasparente,olografico){
    var out=mkCanvas(W,H), o=out.getContext('2d');
    var sheetSil=mkCanvas(W,H), sSil=sheetSil.getContext('2d');   // union of pieces (for the sheen)
    // sheet backing (light, subtle border + shadow)
    var sx=W*0.06, sy=H*0.06, sw=W-sx*2, sh=H-sy*2;
    o.save(); o.shadowColor='rgba(20,16,16,.22)'; o.shadowBlur=22; o.shadowOffsetY=12;
    roundRectPath(o, sx, sy, sw, sh, 16); o.fillStyle='#ffffff'; o.fill(); o.restore();
    roundRectPath(o, sx, sy, sw, sh, 16); o.strokeStyle='rgba(19,17,17,.10)'; o.lineWidth=1.5; o.stroke();

    // grid
    var cols=2, rows=2;
    if(ratio>1.35){ cols=3; rows=2; } else if(ratio<0.74){ cols=2; rows=3; }
    var cellW=sw/cols, cellH=sh/rows;
    var cell=Math.min(cellW,cellH);
    for(var r=0;r<rows;r++) for(var cI=0;cI<cols;cI++){
      var cx=sx+cellW*cI+(cellW-cell)/2, cy=sy+cellH*r+(cellH-cell)/2;
      var st=buildSticker(cell,cell,(art&&!fallback)?'contour':'rect',ratio,cell*0.14,{physW:physW, physH:physH, bordoBianco:true, sfondoIncluso:sfondoIncluso, trasparente:trasparente, olografico:olografico});
      o.drawImage(st.canvas, cx, cy);
      if(st.sil){ sSil.drawImage(st.sil, cx, cy); }
      // dashed kiss-cut outline around the piece
      o.save(); o.translate(cx,cy);
      o.setLineDash([5,4]); o.strokeStyle='rgba(19,17,17,.30)'; o.lineWidth=1.4;
      var b=st.box, gr=Math.max(6,cell*0.03);
      roundRectPath(o, b.x-gr, b.y-gr, b.w+gr*2, b.h+gr*2, Math.min(b.w,b.h)*0.16+gr);
      o.stroke(); o.restore();
    }
    return { canvas:out, sil: (trasparente||olografico) ? sheetSil : null };
  }

  /* ---------- compose one frame ---------- */
  function compose(state, phase){
    var W=RES,H=RES;
    var base=mkCanvas(W,H), b=base.getContext('2d');
    var tipo=state.tipo||'classico';
    var mode=state.shapeMode||'contour';
    var ratio=sizeRatio(state);
    var finitura=(state.finitura && state.finitura!=='standard') ? state.finitura : (tipo==='uv'?'uvgloss':((tipo==='olografico'||tipo==='oro')?'olografica':'standard'));
    var whiteBorder = tipo!=='trasparente';
    var transp = (tipo==='trasparente');
    var olo = (tipo==='olografico' || tipo==='oro');
    var oro = (tipo==='oro');
    var dtf = (tipo==='dtf');
    var vinyl = (tipo==='prespaziato');
    var pad=W*0.085;   // fit-to-frame: longest side ~83% del canvas (inquadratura ravvicinata costante)
    var phys=physSize(state);
    var borderW=Math.max(9, W*0.03);   // decorative glow-halo radius only

    // ---- DTF: transfer film, its own pipeline (no sticker rules) ----
    if(dtf){
      var dres=buildDTF(W,H,ratio,!!state.dtfRemoveBg);
      b.drawImage(dres.canvas,0,0);
      return { base:base, sil:dres.sil, finitura:'standard', mode:'dtf', printOnly:dres.printOnly };
    }
    // ---- Prespaziati: monochrome cut-vinyl silhouette (holes preserved) ----
    if(vinyl){
      var vres=buildVinyl(W,H,ratio, state.vinylColor, !!state.vinylTape);
      b.drawImage(vres.canvas,0,0);
      return { base:base, sil:vres.sil, finitura:'standard', mode:'vinyl', printOnly:vres.printOnly };
    }
    // ---- Stampa-Taglio: die-cut multicolore, profilo per elemento (holes preserved, nessuna fusione) ----
    if(tipo==='stampataglio'){
      var stres=buildStampaTaglio(W,H,{ physW:phys.w, physH:phys.h, border:false, tape:!!state.vinylTape });
      b.drawImage(stres.canvas,0,0);
      return { base:base, sil:stres.sil, finitura:'standard', mode:'stampataglio', printOnly:stres.printOnly };
    }
    // ---- Mezzo Taglio (kiss-cut): backing + vinile con bleed + linea incisa sul profilo ----
    if(tipo==='mezzotaglio'){
      var kres=buildKissCut(W,H,mode,{ physW:phys.w, physH:phys.h, geoRemoveBg:!!state.geoRemoveBg });
      b.drawImage(kres.canvas,0,0);
      return { base:base, sil:kres.sil, finitura:'standard', mode:'mezzotaglio', printOnly:kres.printOnly };
    }

    var sil=null;
    // Trasparente: the dark-bg PLATE is an explicit opt-in only — never the auto default.
    // Otherwise the die-cut margin stays clear film (context bg shows through).
    var sfondoIncluso = transp
      ? (state.sfondoIncluso === true)
      : ((state.sfondoIncluso===undefined || state.sfondoIncluso===null) ? bgIsDark : !!state.sfondoIncluso);
    if(mode==='sheet'){
      var sh=buildSheet(W,H,ratio,sfondoIncluso,phys.w,phys.h,transp,olo);
      b.drawImage(sh.canvas,0,0);
      sil=sh.sil;
    } else {
      var st=buildSticker(W,H,mode,ratio,pad,{physW:phys.w, physH:phys.h, bordoBianco:!!state.bordoBianco, sfondoIncluso:sfondoIncluso, trasparente:transp, olografico:olo, oro:oro, geoRemoveBg:!!state.geoRemoveBg});
      sil=st.sil;
      // glow halo behind (finish)
      if(finitura==='glow'){ b.save(); b.shadowColor='rgba(126,255,142,.9)'; b.shadowBlur=42; var halo=dilate(sil,borderW*0.5,'#9dffb0'); b.drawImage(halo,0,0); b.drawImage(halo,0,0); b.restore(); }
      b.drawImage(st.canvas,0,0);
      // uv gloss on top
      if(finitura==='uvgloss'){ var ov=finishOverlay(W,H,'uvgloss',sil,0); if(ov){ b.save(); b.globalAlpha=ov.__alpha; b.globalCompositeOperation=ov.__blend; b.drawImage(ov,0,0); b.restore(); } }
    }
    // clear-film sheen: subtle diagonal highlight over the whole transparent piece
    if(transp && sil){
      var gl=mkCanvas(W,H), gx=gl.getContext('2d');
      var gg=gx.createLinearGradient(0,0,W,H);
      gg.addColorStop(0,'rgba(255,255,255,0)'); gg.addColorStop(.40,'rgba(255,255,255,.22)');
      gg.addColorStop(.50,'rgba(255,255,255,.05)'); gg.addColorStop(1,'rgba(255,255,255,0)');
      gx.fillStyle=gg; gx.fillRect(0,0,W,H);
      maskTo(gx, sil);
      b.save(); b.globalCompositeOperation='screen'; b.drawImage(gl,0,0); b.restore();
    }
    return { base:base, sil:sil, finitura:finitura, mode:mode };
  }

  /* ---------- paint to targets (+ holo animation) ---------- */
  var cache=null;
  function reducedMotion(){ return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  // signature of what changes the SHAPE/framing — drives the smooth size transition
  function sizeSig(s){ return s ? [s.size,s.customW,s.customH,s.orientamento,s.shapeMode].join('|') : ''; }
  var fadeRAF=null;
  function snapshotTargets(){
    return targets.map(function(t){ var c=mkCanvas(t.canvas.width,t.canvas.height); c.getContext('2d').drawImage(t.canvas,0,0); return c; });
  }
  // brief cross-fade so the shape adapts to the frame without a hard jump (reduced-motion: skipped)
  function crossfade(prev, r, done){
    if(fadeRAF){ cancelAnimationFrame(fadeRAF); fadeRAF=null; }
    if(holoRAF){ cancelAnimationFrame(holoRAF); holoRAF=null; }
    var start=performance.now(), DUR=260;
    (function loop(now){
      var p=Math.min(1,(now-start)/DUR);
      var e=p<0.5 ? 2*p*p : 1-Math.pow(-2*p+2,2)/2;   // easeInOutQuad
      targets.forEach(function(t,i){
        var ctx=t.canvas.getContext('2d'), tw=t.canvas.width, th=t.canvas.height;
        ctx.clearRect(0,0,tw,th);
        if(prev[i]){ ctx.save(); ctx.globalAlpha=1-e; ctx.drawImage(prev[i],0,0); ctx.restore(); }
        ctx.save(); ctx.globalAlpha=e; ctx.drawImage(r.base,0,0,tw,th); ctx.restore();
      });
      if(p<1){ fadeRAF=requestAnimationFrame(loop); }
      else { fadeRAF=null; drawToTargets(r.base,r.sil,r.finitura,r.mode,0); if(done) done(); }
    })(start);
  }
  function paint(state){
    var prev = (cache && !reducedMotion() && sizeSig(cache.state)!==sizeSig(state)) ? snapshotTargets() : null;
    var r=compose(state,0);
    lastBase=r.base;
    lastPrintOnly = r.printOnly || null;
    cache={ base:r.base, sil:r.sil, finitura:r.finitura, mode:r.mode, state:state };
    if(prev){ crossfade(prev, r, function(){ manageAnim(r.finitura,r.mode); }); }
    else { drawToTargets(r.base,r.sil,r.finitura,r.mode,0); manageAnim(r.finitura,r.mode); }
  }
  function drawToTargets(base,sil,finitura,mode,phase){
    targets.forEach(function(t){
      var ctx=t.canvas.getContext('2d');
      ctx.clearRect(0,0,t.canvas.width,t.canvas.height);
      ctx.drawImage(base,0,0,t.canvas.width,t.canvas.height);
      if(mode!=='sheet' && sil && (finitura==='olografica'||finitura==='laminato')){
        var ov=finishOverlay(RES,RES,finitura,sil,phase);
        if(ov){ ctx.save(); ctx.globalAlpha=ov.__alpha; ctx.globalCompositeOperation=ov.__blend; ctx.drawImage(ov,0,0,t.canvas.width,t.canvas.height); ctx.restore(); }
      }
    });
  }
  function manageAnim(finitura,mode){
    if(holoRAF){ cancelAnimationFrame(holoRAF); holoRAF=null; }
    if(finitura!=='olografica' || mode==='sheet' || !cache) return;
    if(window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var start=performance.now();
    (function loop(now){ var phase=((now-start)/2600)%1; drawToTargets(cache.base,cache.sil,'olografica',cache.mode,phase); holoRAF=requestAnimationFrame(loop); })(start);
  }

  /* ---------- public API ---------- */
  function addCanvas(canvas){ if(!canvas) return; canvas.width=RES; canvas.height=RES; if(!targets.find(function(t){return t.canvas===canvas;})) targets.push({canvas:canvas}); if(hasImg && lastState) paint(lastState); }

  function loadImage(src){
    return new Promise(function(res,rej){
      var img=new Image(); img.crossOrigin='anonymous';
      img.onload=function(){ res(img); }; img.onerror=rej;
      if(src instanceof Blob || (typeof File!=='undefined' && src instanceof File)){ var fr=new FileReader(); fr.onload=function(){ img.src=fr.result; }; fr.readAsDataURL(src); }
      else { img.src=src; }
    });
  }

  function setImage(src){
    fire('onProcessing');
    return loadImage(src).then(function(img){
      return new Promise(function(res){
        // defer heavy work one frame so the "processing" UI can paint
        requestAnimationFrame(function(){
          processImage(img);
          hasImg=true;
          if(lastState) paint(lastState);
          if(fallback) fire('onFallback'); else fire('onReady');
          res(true);
        });
      });
    });
  }

  var WowStickerCutPreview = {
    init:function(opts){ opts=opts||{}; if(opts.onProcessing)cbs.onProcessing=opts.onProcessing; if(opts.onReady)cbs.onReady=opts.onReady; if(opts.onFallback)cbs.onFallback=opts.onFallback; if(opts.canvas)addCanvas(opts.canvas); if(opts.canvases)opts.canvases.forEach(addCanvas); return WowStickerCutPreview; },
    on:function(name,fn){ if(name in cbs) cbs[name]=fn; return WowStickerCutPreview; },
    addCanvas:addCanvas,
    register:addCanvas,                       // back-compat name
    setImage:setImage,
    hasImage:function(){ return hasImg; },
    imageAspect:function(){ return artOrig ? artOrig.width/artOrig.height : null; },
    isFallback:function(){ return fallback; },
    hasBackgroundPlate:function(){ return !!(bgRemovable && bgIsDark); },
    hasRemovableBg:function(){ return !!bgRemovable; },
    hasColoredBg:function(){ return !!(bgRemovable && bgIsDark); },
    bgColorCss:function(){ return bgColor ? rgbStr(bgColor) : null; },
    subjectColorCount:subjectColorCount,
    kissBorderMode:function(){ return kissBorderMode; },
    minDetailMm:minDetailMm,
    lightGraphicRatio:function(){ return lightRatio; },
    setCut:function(mode){ lastState=Object.assign({},lastState,{shapeMode:mode}); if(hasImg) paint(lastState); },
    setSize:function(w,h){ lastState=Object.assign({},lastState,{size:'custom',customW:w,customH:h}); if(hasImg) paint(lastState); },
    setMaterial:function(tipo){ lastState=Object.assign({},lastState,{tipo:tipo}); if(hasImg) paint(lastState); },
    update:function(state){ lastState=Object.assign({},lastState,state||{}); if(hasImg) paint(lastState); },
    render:function(canvas){ addCanvas(canvas); },
    getCutoutDataURL:function(){ try{ var src = lastPrintOnly || lastBase; return src ? src.toDataURL('image/png') : null; }catch(e){ return null; } },
    clear:function(){ art=null; artOrig=null; artTransp=null; artOrigTransp=null; vinylMask=null; hasAlpha=false; lightRatio=0; artBBox=null; hasImg=false; fallback=false; bgColor=null; bgIsDark=false; bgRemovable=false; lastBase=null; lastPrintOnly=null; if(holoRAF)cancelAnimationFrame(holoRAF); targets.forEach(function(t){ t.canvas.getContext('2d').clearRect(0,0,t.canvas.width,t.canvas.height); }); }
  };

  window.WowStickerCutPreview = WowStickerCutPreview;
  window.WowPreview = WowStickerCutPreview;   // compatibility alias for app.js
})();
